// src/pages/Customer/RestaurantList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from "../../utils/api";
import { toast } from "react-toastify";
import { 
  FaStar, 
  FaClock, 
  FaMapMarkerAlt, 
  FaSearch,
  FaFilter,
  FaShoppingCart
} from 'react-icons/fa';

const CustomerDashBoard = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch restaurants from your API
  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const res = await api.get("restaurant/restaurant/all");
      const restaurantData = res.data?.reataurants || [];
      
      // Transform API data to match customer UI needs
      const transformedRestaurants = restaurantData.map(restaurant => ({
        id: restaurant.id,
        name: restaurant.business_name,
        cuisine: "Multi-cuisine", // You might want to add cuisine field to your API
        rating: 4.3, // Default rating or fetch from API
        deliveryTime: "30-40 min",
        price: "₹200 for one",
        image: restaurant.restaurant_images?.[0] || "/default-restaurant.jpg",
        discount: "20% OFF", // You can calculate based on offers
        isOpen: restaurant.is_active,
        address: `${restaurant.line1}, ${restaurant.city}, ${restaurant.state}`,
        contact: restaurant.contact_number,
        // Include original API data for details page
        originalData: restaurant
      }));
      
      setRestaurants(transformedRestaurants);
    } catch (err) {
      toast.error("Failed to fetch restaurants");
      console.error("Error fetching restaurants:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  // Filter and sort restaurants
  const filteredRestaurants = restaurants
    .filter(restaurant => {
      const matchesSearch = restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           restaurant.cuisine.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || 
                             restaurant.cuisine.toLowerCase().includes(selectedCategory);
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'rating': return b.rating - a.rating;
        case 'delivery': return parseInt(a.deliveryTime) - parseInt(b.deliveryTime);
        case 'cost': return parseInt(a.price) - parseInt(b.price);
        default: return 0;
      }
    });

  const categories = [
    { id: 'all', name: 'All' },
    { id: 'burgers', name: 'Burgers' },
    { id: 'pizza', name: 'Pizza' },
    { id: 'asian', name: 'Asian' },
    { id: 'indian', name: 'Indian' },
    { id: 'desserts', name: 'Desserts' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/customer" className="text-2xl font-bold text-red-600">Foodie</Link>
              <div className="relative">
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for restaurants or food..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-80 pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/customer/profile" className="text-gray-700 hover:text-red-600">Profile</Link>
              <Link to="/customer/cart" className="flex items-center gap-2 text-gray-700 hover:text-red-600">
                <FaShoppingCart /> Cart (0)
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex gap-4 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                selectedCategory === category.id
                  ? 'bg-red-600 text-white'
                  : 'bg-white text-gray-700 border hover:bg-gray-50'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold">
            All Restaurants {filteredRestaurants.length > 0 && `(${filteredRestaurants.length})`}
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-gray-600 flex items-center gap-2">
              <FaFilter /> Sort by:
            </span>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="rating">Rating: High to Low</option>
              <option value="delivery">Delivery Time</option>
              <option value="cost">Cost: Low to High</option>
            </select>
          </div>
        </div>

        {filteredRestaurants.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🏪</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No restaurants found</h3>
            <p className="text-gray-600">
              {searchQuery ? `No results for "${searchQuery}"` : 'No restaurants available at the moment'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredRestaurants.map(restaurant => (
              <Link 
                key={restaurant.id} 
                to={`/customer/restaurant/${restaurant.id}`}
                state={{ restaurantData: restaurant.originalData }}
                className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="relative">
                  <img 
                    src={restaurant.image} 
                    alt={restaurant.name}
                    className="w-full h-48 object-cover rounded-t-xl"
                    onError={(e) => {
                      e.target.src = '/default-restaurant.jpg';
                    }}
                  />
                  <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 rounded text-sm font-semibold">
                    {restaurant.discount}
                  </div>
                  <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm flex items-center gap-1">
                    <FaStar className="text-yellow-400" />
                    {restaurant.rating}
                  </div>
                  {!restaurant.isOpen && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-t-xl flex items-center justify-center">
                      <span className="text-white font-semibold bg-red-600 px-3 py-1 rounded">CLOSED</span>
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  <h3 className="font-bold text-lg mb-1 truncate">{restaurant.name}</h3>
                  <p className="text-gray-600 text-sm mb-2 truncate">{restaurant.cuisine}</p>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <FaMapMarkerAlt className="mr-1 text-red-500" />
                    <span className="truncate">{restaurant.address}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <FaClock className="text-green-500" />
                      {restaurant.deliveryTime}
                    </span>
                    <span>•</span>
                    <span className="font-semibold">{restaurant.price}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashBoard;